// CheckoutPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Divider, Grid, MenuItem, Radio, Snackbar, Stack, TextField, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentIcon from "@mui/icons-material/Payment";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import HomeIcon from "@mui/icons-material/Home";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import LocalDiningRoundedIcon from "@mui/icons-material/LocalDiningRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ApiServices from "../../Utils/ApiServices";
import StripePaymentDialog from "./StripePaymentDialog";

const BRAND = {
  redDeep: "#7A0E0E",
  red: "#B71C1C",
  orange: "#E64A19",
  amber: "#FFB300",
  cream: "#FFF8F0",
  creamDeep: "#FBE9D2",
  charcoal: "#2A1810",
  muted: "#8B6F60",
  gradPrimary: "linear-gradient(135deg, #7A0E0E 0%, #B71C1C 45%, #E64A19 100%)",
  gradAmber: "linear-gradient(135deg, #FF8F00 0%, #FFB300 50%, #FFC107 100%)",
  gradCream: "linear-gradient(180deg, #FFF8F0 0%, #FBE9D2 100%)",
};

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || "CLT1006";

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const getAccessToken = () => localStorage.getItem("access_token") || "";
const getCustomerId = () =>
  localStorage.getItem("customer_id") ||
  localStorage.getItem("customerId") ||
  localStorage.getItem("logged_customer_id") ||
  localStorage.getItem("user_id") ||
  "";

const formatDollar = (value) => `$${Number(value || 0).toFixed(2)}`;
const moneyFromQuote = (quote) => Number(quote?.fee_display || 0);

const serviceOptionsConfig = {
  pickup: {
    label: "Pickup",
    description: "Collect your order from the restaurant.",
    icon: <StorefrontIcon />,
  },
  delivery: {
    label: "Delivery",
    description: "Restaurant will deliver the order to your address.",
    icon: <LocalShippingIcon />,
  },
  dine_in: {
    label: "Dine In",
    description: "Eat your order at the restaurant.",
    icon: <RestaurantIcon />,
  },
};

const emptyAddressForm = {
  label: "home",
  recipient_name: "",
  recipient_phone: "",
  street_address: "",
  landmark: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  is_default: false,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    bgcolor: "#fff",
    "& fieldset": { borderColor: "rgba(122,14,14,0.2)" },
    "&:hover fieldset": { borderColor: BRAND.red },
    "&.Mui-focused fieldset": { borderColor: BRAND.redDeep, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: BRAND.redDeep },
};

const SectionCard = ({ children, sx = {}, ...rest }) => (
  <MotionCard
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, ease: "easeOut" }}
    sx={{
      borderRadius: 5,
      background: "rgba(255,255,255,0.92)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(122,14,14,0.08)",
      boxShadow: "0 14px 40px rgba(122,14,14,0.1)",
      overflow: "hidden",
      position: "relative",
      ...sx,
    }}
    {...rest}
  >
    {children}
  </MotionCard>
);

const SectionHeader = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={1.75} alignItems="center" sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: BRAND.gradPrimary,
        display: "grid",
        placeItems: "center",
        boxShadow: "0 8px 20px rgba(122,14,14,0.35)",
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        sx={{
          fontFamily: "'Playfair Display', serif",
          fontWeight: 800,
          fontSize: "1.25rem",
          color: BRAND.charcoal,
          lineHeight: 1.1,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Stack>
);

const AmbientBg = () => (
  <Box aria-hidden sx={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
    <Box
      sx={{
        position: "absolute",
        top: -120,
        right: -100,
        width: 380,
        height: 380,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,179,0,0.28) 0%, rgba(255,179,0,0) 70%)",
        filter: "blur(10px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        bottom: -140,
        left: -100,
        width: 420,
        height: 420,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(230,74,25,0.22) 0%, rgba(230,74,25,0) 70%)",
        filter: "blur(12px)",
      }}
    />
  </Box>
);

const LoadingScreen = () => (
  <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", background: BRAND.gradCream, position: "relative" }}>
    <AmbientBg />
    <Stack spacing={2} alignItems="center" sx={{ zIndex: 1 }}>
      <MotionBox
        animate={{ rotate: 360 }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: BRAND.gradPrimary,
          display: "grid",
          placeItems: "center",
          boxShadow: "0 14px 30px rgba(122,14,14,0.4)",
        }}
      >
        <RestaurantMenuIcon sx={{ color: "#fff", fontSize: 34 }} />
      </MotionBox>
      <Typography sx={{ color: BRAND.charcoal, fontWeight: 700 }}>
        Preparing your checkout…
      </Typography>
    </Stack>
  </Box>
);

const EmptyState = ({ type, navigate }) => {
  const isLogin = type === "login";
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, background: BRAND.gradCream, minHeight: "100vh", position: "relative" }}>
      <AmbientBg />
      <Box sx={{ maxWidth: isLogin ? 600 : 900, mx: "auto", position: "relative", zIndex: 1 }}>
        {!isLogin && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2, borderRadius: 999, color: BRAND.redDeep, fontWeight: 700 }}
          >
            Back
          </Button>
        )}

        <SectionCard>
          <CardContent sx={{ textAlign: "center", py: 5 }}>
            <Box
              sx={{
                width: 84,
                height: 84,
                mx: "auto",
                mb: 2,
                borderRadius: "50%",
                background: isLogin ? BRAND.gradPrimary : BRAND.gradAmber,
                display: "grid",
                placeItems: "center",
                boxShadow: "0 12px 28px rgba(122,14,14,0.35)",
              }}
            >
              {isLogin ? (
                <LockOutlinedIcon sx={{ color: "#fff", fontSize: 38 }} />
              ) : (
                <RestaurantMenuIcon sx={{ color: "#fff", fontSize: 40 }} />
              )}
            </Box>

            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, fontSize: "1.5rem", color: BRAND.charcoal }}>
              {isLogin ? "Sign in to continue" : "Your cart is empty"}
            </Typography>

            <Typography sx={{ mt: 1, mb: isLogin ? 0 : 3, color: BRAND.muted }}>
              {isLogin ? "Please login to continue checkout." : "Add delicious items to start your order."}
            </Typography>

            {!isLogin && (
              <Button
                variant="contained"
                onClick={() => navigate("/menu")}
                startIcon={<RestaurantMenuIcon />}
                sx={{
                  borderRadius: 999,
                  py: 1.25,
                  px: 3,
                  fontWeight: 800,
                  background: BRAND.gradPrimary,
                  boxShadow: "0 10px 24px rgba(122,14,14,0.35)",
                }}
              >
                Browse Menu
              </Button>
            )}
          </CardContent>
        </SectionCard>
      </Box>
    </Box>
  );
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const customerId = useMemo(() => getCustomerId(), []);
  const isLoggedIn = localStorage.getItem("is_logged") === "true";

  const [cart, setCart] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [availableServices, setAvailableServices] = useState(["pickup", "delivery"]);
  const [selectedService, setSelectedService] = useState("pickup");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe");

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState(emptyAddressForm);
  const [addressErrors, setAddressErrors] = useState({});

  const [pickupAddress, setPickupAddress] = useState("");
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");

  const [loading, setLoading] = useState(true);
  const [savingAddress, setSavingAddress] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [clientApiFailed, setClientApiFailed] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
const [paymentIntentData, setPaymentIntentData] = useState(null);
const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
const [paymentIntentError, setPaymentIntentError] = useState("");

  const authConfig = useCallback(() => ({ Authorization: `Bearer ${getAccessToken()}` }), []);
  const showMessage = (message, severity = "info") => setSnackbar({ open: true, message, severity });

  const selectedAddress = addresses.find((a) => a.id === selectedAddressId);

  const itemCount = useMemo(
    () => cart?.items?.reduce((sum, item) => sum + Number(item.quantity || 0), 0) || 0,
    [cart]
  );

  const deliveryFee = selectedService === "delivery" ? moneyFromQuote(deliveryQuote) : 0;
  const finalTotal = Number(cart?.total || 0) + deliveryFee;

  const fetchCart = async () => {
    const res = await ApiServices.get(`/api/v1/customers/${customerId}/cart/`, authConfig());
    return res?.data || null;
  };

  const fetchClientFullDetails = async () => {
    const res = await ApiServices.get(`/api/v1/clients/${CLIENT_ID}/full/`);
    return res?.data || null;
  };

  const fetchAddresses = async () => {
    const res = await ApiServices.get(`/api/v1/customers/${customerId}/addresses/`, authConfig());
    const list = Array.isArray(res?.data) ? res.data : [];

    setAddresses(list);

    const selected = list.find((a) => a.is_default) || list[0];
    setSelectedAddressId(selected?.id || "");

    return list;
  };

  const getAvailableServicesFromClient = (client) => {
    const serviceTypes = client?.service_types;
    const services = Object.keys(serviceTypes || {}).filter(
      (key) => ["pickup", "delivery", "dine_in"].includes(key) && serviceTypes[key] === true
    );
    return services.length ? services : ["pickup", "delivery"];
  };

  const syncCartServiceType = async (service) => {
    await ApiServices.patch(
      `/api/v1/customers/${customerId}/cart/`,
      { service_type: service, location_id: cart?.location_id || null },
      authConfig()
    );
  };

  const fetchPickupLocation = async () => {
    try {
      const res = await ApiServices.get(`/api/v1/clients/${CLIENT_ID}/locations/`);
      const locations = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const loc = locations[0];

      if (!loc) return;

      setPickupAddress(
        [loc.address_line1, loc.street, loc.city, loc.state, loc.postal_code, loc.country]
          .filter(Boolean)
          .join(" ")
      );
    } catch (err) {
      console.error("Failed to fetch pickup location:", err);
    }
  };

  const buildDropoffAddress = (address) =>
    address
      ? [address.street_address, address.landmark, address.city, address.state, address.pincode, address.country]
          .filter(Boolean)
          .join(" ")
      : "";

  const getDeliveryQuote = useCallback(
    async (dropoffAddress) => {
      if (!pickupAddress || !dropoffAddress || !customerId || selectedService !== "delivery") return;

      try {
        setQuoteLoading(true);
        setQuoteError("");

        const res = await ApiServices.post(
          `/api/v1/customers/${customerId}/cart/delivery-quote/`,
          { pickup_address: pickupAddress, dropoff_address: dropoffAddress },
          authConfig()
        );

        setDeliveryQuote(res?.data || null);
      } catch (err) {
        setDeliveryQuote(null);
        setQuoteError(err?.response?.data?.detail || "Failed to calculate delivery fee.");
      } finally {
        setQuoteLoading(false);
      }
    },
    [pickupAddress, customerId, selectedService, authConfig]
  );

  const loadCheckoutData = async () => {
    if (!isLoggedIn || !customerId) {
      setLoading(false);
      showMessage("Please login to continue checkout.", "error");
      return;
    }

    try {
      setLoading(true);

      const cartData = await fetchCart();
      setCart(cartData);

      let services = ["pickup", "delivery"];

      try {
        const client = await fetchClientFullDetails();
        setClientData(client);
        services = getAvailableServicesFromClient(client);
        setClientApiFailed(false);
      } catch {
        setClientApiFailed(true);
      }

      setAvailableServices(services);

      const cartService = cartData?.service_type;
      const defaultService = services.includes(cartService) ? cartService : services[0];
      setSelectedService(defaultService);

      if (cartData?.id && cartService !== defaultService) {
        await ApiServices.patch(
          `/api/v1/customers/${customerId}/cart/`,
          { service_type: defaultService, location_id: cartData?.location_id || null },
          authConfig()
        );
        setCart(await fetchCart());
      }

      await fetchAddresses();
    } catch (err) {
      setCart(null);
      showMessage(err?.response?.data?.detail || "Failed to load checkout details.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCheckoutData();
    fetchPickupLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedService !== "delivery") {
      setDeliveryQuote(null);
      setQuoteError("");
      return;
    }

    const dropoff = buildDropoffAddress(selectedAddress);
    if (dropoff) getDeliveryQuote(dropoff);
  }, [selectedAddressId, addresses, selectedService, pickupAddress, selectedAddress, getDeliveryQuote]);

  const handleServiceChange = async (service) => {
    if (!availableServices.includes(service)) return;

    try {
      setSelectedService(service);
      if (service !== "delivery") {
        setDeliveryQuote(null);
        setQuoteError("");
      }

      await ApiServices.patch(
        `/api/v1/customers/${customerId}/cart/`,
        { service_type: service, location_id: cart?.location_id || null },
        authConfig()
      );

      setCart(await fetchCart());
      showMessage("Service type updated.", "success");
    } catch (err) {
      showMessage(err?.response?.data?.detail || "Failed to update service type.", "error");
    }
  };

  const handleAddressFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    let finalValue = type === "checkbox" ? checked : value;

    if (name === "recipient_phone") finalValue = value.replace(/\D/g, "").slice(0, 10);
    if (name === "pincode") finalValue = value.replace(/\D/g, "").slice(0, 6);

    setAddressForm((prev) => ({ ...prev, [name]: finalValue }));
    setAddressErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateAddressForm = () => {
    const errors = {};

    if (!addressForm.street_address.trim()) errors.street_address = "Street address is required";
    if (!addressForm.city.trim()) errors.city = "City is required";
    if (!addressForm.state.trim()) errors.state = "State is required";

    if (!addressForm.pincode.trim()) errors.pincode = "Pincode is required";
    else if (!/^\d{5,6}$/.test(addressForm.pincode.trim())) errors.pincode = "Enter valid 5 or 6 digit pincode";

    if (addressForm.recipient_phone && !/^[6-9]\d{9}$/.test(addressForm.recipient_phone)) {
      errors.recipient_phone = "Enter valid 10 digit mobile number";
    }

    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveAddress = async () => {
    if (!validateAddressForm()) return;

    try {
      setSavingAddress(true);

      const payload = Object.fromEntries(
        Object.entries(addressForm).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
      );

      const res = await ApiServices.post(`/api/v1/customers/${customerId}/addresses/`, payload, authConfig());
      await fetchAddresses();

      if (res?.data?.id) setSelectedAddressId(res.data.id);

      setAddressForm(emptyAddressForm);
      setAddressErrors({});
      setShowAddressForm(false);
      showMessage("Address added successfully.", "success");
    } catch (err) {
      showMessage(err?.response?.data?.detail || "Failed to add address.", "error");
    } finally {
      setSavingAddress(false);
    }
  };

  const createStripePaymentIntent = async () => {
  try {
    setPaymentIntentLoading(true);
    setPaymentIntentError("");
    setPaymentIntentData(null);
    setStripeDialogOpen(true);

    const payload = {};

    if (selectedService === "delivery") {
      if (!deliveryQuote?.fee_display) {
        throw new Error("Delivery fee is not calculated yet.");
      }

      payload.delivery_fee = deliveryQuote.fee_display;
    }

    const response = await ApiServices.post(
      `/api/v1/customers/${customerId}/payment/create-intent/`,
      payload,
      authConfig()
    );

    setPaymentIntentData(response?.data || null);
  } catch (error) {
    setPaymentIntentError(
      error?.response?.data?.detail ||
        error?.message ||
        "Failed to start Stripe payment."
    );
  } finally {
    setPaymentIntentLoading(false);
  }
};

const placeOrder = async () => {
  if (!cart?.items?.length) {
    showMessage("Cart is empty.", "error");
    return;
  }

  if (selectedService === "delivery" && !selectedAddressId) {
    showMessage("Please select or add a delivery address.", "error");
    return;
  }

  if (selectedService === "delivery" && !deliveryQuote?.quote_id) {
    showMessage("Please wait until delivery fee is calculated.", "error");
    return;
  }

  if (!paymentMethod) {
    showMessage("Please select payment method.", "error");
    return;
  }

  if (paymentMethod === "stripe") {
    await syncCartServiceType(selectedService);
    await createStripePaymentIntent();
    return;
  }

  try {
    setPlacingOrder(true);
    await syncCartServiceType(selectedService);

    const payload = {
      payment_method: "cod",
      special_instructions: specialInstructions.trim(),
    };

    if (selectedService === "delivery") {
      payload.quote_id = deliveryQuote.quote_id;
    }

    const res = await ApiServices.post(
      `/api/v1/customers/${customerId}/orders/place/`,
      payload,
      authConfig()
    );

    showMessage("Order placed successfully.", "success");
    navigate(res?.data?.id ? `/orders/${res.data.id}` : "/orders");
  } catch (err) {
    showMessage(
      err?.response?.data?.detail || "Failed to place order.",
      "error"
    );
  } finally {
    setPlacingOrder(false);
  }
};

const handleStripePaymentSuccess = async ({ payment_intent_id }) => {
  try {
    setPlacingOrder(true);

    const payload = {
      payment_method: "stripe",
      payment_intent_id,
      special_instructions: specialInstructions.trim(),
    };

    if (selectedService === "delivery") {
      payload.quote_id = deliveryQuote.quote_id;
    }

    const res = await ApiServices.post(
      `/api/v1/customers/${customerId}/orders/place/`,
      payload,
      authConfig()
    );

    setStripeDialogOpen(false);
    showMessage("Payment successful. Order placed successfully.", "success");

    navigate(res?.data?.id ? `/orders/${res.data.id}` : "/orders");
  } catch (error) {
    showMessage(
      error?.response?.data?.detail ||
        "Payment was successful, but order placement failed. Please contact support.",
      "error"
    );
  } finally {
    setPlacingOrder(false);
  }
};

  // const placeOrder = async () => {
  //   if (!cart?.items?.length) return showMessage("Cart is empty.", "error");
  //   if (selectedService === "delivery" && !selectedAddressId) {
  //     return showMessage("Please select or add a delivery address.", "error");
  //   }
  //   if (selectedService === "delivery" && !deliveryQuote?.quote_id) {
  //     return showMessage("Please wait until delivery fee is calculated.", "error");
  //   }

  //   try {
  //     setPlacingOrder(true);
  //     await syncCartServiceType(selectedService);

  //     const payload = {
  //       payment_method: paymentMethod,
  //       special_instructions: specialInstructions.trim(),
  //     };

  //     if (selectedService === "delivery") {
  //       payload.delivery_quote_id = deliveryQuote.quote_id;
  //     }

  //     const res = await ApiServices.post(
  //       `/api/v1/customers/${customerId}/orders/place/`,
  //       payload,
  //       authConfig()
  //     );

  //     showMessage("Order placed successfully.", "success");
  //     navigate(res?.data?.id ? `/orders/${res.data.id}` : "/orders");
  //   } catch (err) {
  //     showMessage(err?.response?.data?.detail || "Failed to place order.", "error");
  //   } finally {
  //     setPlacingOrder(false);
  //   }
  // };

  if (loading) return <LoadingScreen />;
  if (!isLoggedIn) return <EmptyState type="login" navigate={navigate} />;
  if (!cart?.items?.length) return <EmptyState type="cart" navigate={navigate} />;

  return (
    <Box sx={{ background: BRAND.gradCream, minHeight: "100vh", position: "relative", pb: 6 }}>
      <AmbientBg />

      <Box
        sx={{
          position: "relative",
          background: BRAND.gradPrimary,
          color: "#fff",
          px: { xs: 2, md: 4 },
          pt: { xs: 3, md: 5 },
          pb: { xs: 6, md: 9 },
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(122,14,14,0.3)",
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto", position: "relative", zIndex: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 2,
              color: "#fff",
              borderRadius: 999,
              px: 2,
              bgcolor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)",
              fontWeight: 700,
            }}
          >
            Back to Cart
          </Button>

          <MotionBox initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <LocalDiningRoundedIcon sx={{ fontSize: 18, opacity: 0.9 }} />
              <Typography sx={{ opacity: 0.9, fontWeight: 600, letterSpacing: 1, fontSize: 12 }}>
                FINAL STEP • SECURE CHECKOUT
              </Typography>
            </Stack>
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: { xs: "2rem", md: "2.75rem" }, lineHeight: 1.05 }}>
              Checkout
            </Typography>
            <Typography sx={{ mt: 1, opacity: 0.92, maxWidth: 620 }}>
              Review your order, choose service type, select address and place your order.
            </Typography>
          </MotionBox>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 }, mt: { xs: -4, md: -6 }, position: "relative", zIndex: 2 }}>
        {clientApiFailed && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 3, fontWeight: 600 }}>
            Restaurant service details could not be loaded. Pickup and Delivery are shown by default.
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <SectionCard>
                <Box sx={{ height: 6, background: BRAND.gradPrimary }} />
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <SectionHeader icon={<RestaurantIcon />} title="Restaurant" subtitle="Your order will be prepared with love" />
                  <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: BRAND.charcoal }}>
                    {clientData?.details?.trading_name || clientData?.legal_name || "Restaurant"}
                  </Typography>
                  {clientData?.details?.tagline && (
                    <Typography sx={{ color: BRAND.muted, fontStyle: "italic", mt: 0.25 }}>
                      “{clientData.details.tagline}”
                    </Typography>
                  )}
                  <Chip size="small" label={`Client ID: ${CLIENT_ID}`} sx={{ mt: 1.5, bgcolor: BRAND.creamDeep, color: BRAND.charcoal, fontWeight: 700 }} />
                </CardContent>
              </SectionCard>

              <SectionCard>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <SectionHeader icon={<LocalShippingIcon />} title="Choose Service Type" subtitle="How would you like to enjoy your meal?" />

                  <Grid container spacing={2}>
                    {availableServices.map((service, idx) => {
                      const config = serviceOptionsConfig[service] || {};
                      const selected = selectedService === service;

                      return (
                        <Grid item xs={12} sm={6} key={service}>
                          <MotionCard
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.06 }}
                            whileHover={{ y: -4 }}
                            onClick={() => handleServiceChange(service)}
                            sx={{
                              cursor: "pointer",
                              borderRadius: 4,
                              border: selected ? `2px solid ${BRAND.red}` : "1px solid rgba(122,14,14,0.1)",
                              background: selected ? "linear-gradient(135deg, #FFF1E0 0%, #FFE0A3 100%)" : "#fff",
                              height: "100%",
                              transition: "all .25s",
                              boxShadow: selected ? "0 12px 28px rgba(183,28,28,0.18)" : "0 4px 14px rgba(0,0,0,0.04)",
                            }}
                          >
                            <CardContent>
                              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Radio checked={selected} sx={{ p: 0.5, color: BRAND.muted, "&.Mui-checked": { color: BRAND.red } }} />
                                <Box sx={{ flex: 1 }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        display: "grid",
                                        placeItems: "center",
                                        background: selected ? BRAND.gradPrimary : BRAND.cream,
                                        color: selected ? "#fff" : BRAND.redDeep,
                                        "& svg": { fontSize: 18 },
                                      }}
                                    >
                                      {config.icon}
                                    </Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: BRAND.charcoal }}>
                                      {config.label || service}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.75 }}>
                                    {config.description}
                                  </Typography>
                                  {selected && (
                                    <Chip
                                      icon={<CheckCircleIcon />}
                                      label="Selected"
                                      size="small"
                                      sx={{ mt: 1.25, background: BRAND.gradAmber, color: "#fff", fontWeight: 700, "& .MuiChip-icon": { color: "#fff" } }}
                                    />
                                  )}
                                </Box>
                              </Stack>
                            </CardContent>
                          </MotionCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>    
              </SectionCard>

              <AnimatePresence>
                {selectedService === "delivery" && (
                  <MotionBox initial={{ opacity: 0, y: 20, height: 0 }} animate={{ opacity: 1, y: 0, height: "auto" }} exit={{ opacity: 0, y: -20, height: 0 }}>
                    <SectionCard>
                      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} sx={{ mb: 2 }}>
                          <SectionHeader icon={<HomeIcon />} title="Delivery Address" subtitle="Select your delivery address or add a new one" />
                          <Button
                            variant="outlined"
                            startIcon={<AddLocationAltIcon />}
                            onClick={() => setShowAddressForm((prev) => !prev)}
                            sx={{ borderRadius: 999, borderColor: BRAND.red, color: BRAND.redDeep, fontWeight: 700 }}
                          >
                            {showAddressForm ? "Close Form" : "Add Address"}
                          </Button>
                        </Stack>

                        {addresses.length === 0 && !showAddressForm && (
                          <Alert severity="warning" sx={{ borderRadius: 3, mb: 2, fontWeight: 600 }}>
                            No address found. Please add an address for delivery.
                          </Alert>
                        )}

                        {addresses.length > 0 && (
                          <Grid container spacing={2}>
                            {addresses.map((address, idx) => {
                              const selected = selectedAddressId === address.id;

                              return (
                                <Grid item xs={12} sm={6} key={address.id}>
                                  <MotionCard
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.05 }}
                                    whileHover={{ y: -3 }}
                                    onClick={() => setSelectedAddressId(address.id)}
                                    sx={{
                                      cursor: "pointer",
                                      borderRadius: 4,
                                      border: selected ? `2px solid ${BRAND.red}` : "1px solid rgba(122,14,14,0.1)",
                                      background: selected ? "linear-gradient(135deg, #FFF1E0 0%, #FFE0A3 100%)" : "#fff",
                                      height: "100%",
                                    }}
                                  >
                                    <CardContent>
                                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <Radio checked={selected} sx={{ p: 0.5, color: BRAND.muted, "&.Mui-checked": { color: BRAND.red } }} />
                                        <Box sx={{ flex: 1 }}>
                                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <HomeIcon fontSize="small" sx={{ color: BRAND.redDeep }} />
                                            <Typography sx={{ fontWeight: 800, textTransform: "capitalize", color: BRAND.charcoal }}>
                                              {address.label || "Address"}
                                            </Typography>
                                            {address.is_default && (
                                              <Chip label="Default" size="small" sx={{ background: BRAND.gradAmber, color: "#fff", fontWeight: 700, height: 20 }} />
                                            )}
                                          </Stack>

                                          <Typography variant="body2" sx={{ mt: 1, color: BRAND.charcoal }}>
                                            {address.street_address}
                                          </Typography>
                                          {address.landmark && (
                                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                              Landmark: {address.landmark}
                                            </Typography>
                                          )}
                                          <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                            {address.city}, {address.state} - {address.pincode}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                            {address.country}
                                          </Typography>
                                          {(address.recipient_name || address.recipient_phone) && (
                                            <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                              Recipient: {address.recipient_name || "-"} {address.recipient_phone ? `(${address.recipient_phone})` : ""}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Stack>
                                    </CardContent>
                                  </MotionCard>
                                </Grid>
                              );
                            })}
                          </Grid>
                        )}

                        <AnimatePresence>
                          {showAddressForm && (
                            <MotionBox initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} sx={{ mt: 3 }}>
                              <Divider sx={{ mb: 3, borderStyle: "dashed", borderColor: "rgba(122,14,14,0.2)" }} />
                              <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, fontSize: "1.15rem", color: BRAND.charcoal, mb: 2 }}>
                                Add New Address
                              </Typography>

                              <Grid container spacing={2}>
                                {[
                                  { name: "label", label: "Label", select: true, xs: 12, sm: 4 },
                                  { name: "recipient_name", label: "Recipient Name", xs: 12, sm: 4, helper: "Optional" },
                                  { name: "recipient_phone", label: "Recipient Phone", xs: 12, sm: 4, helper: "Optional" },
                                  { name: "street_address", label: "Street Address", xs: 12, multiline: true },
                                  { name: "landmark", label: "Landmark", xs: 12, sm: 6, helper: "Optional" },
                                  { name: "city", label: "City", xs: 12, sm: 6 },
                                  { name: "state", label: "State", xs: 12, sm: 4 },
                                  { name: "pincode", label: "Pincode", xs: 12, sm: 4 },
                                  { name: "country", label: "Country", xs: 12, sm: 4 },
                                ].map((field) => (
                                  <Grid item xs={field.xs} sm={field.sm} key={field.name}>
                                    <TextField
                                      select={field.select}
                                      fullWidth
                                      multiline={field.multiline}
                                      minRows={field.multiline ? 2 : undefined}
                                      label={field.label}
                                      name={field.name}
                                      value={addressForm[field.name]}
                                      onChange={handleAddressFormChange}
                                      error={Boolean(addressErrors[field.name])}
                                      helperText={addressErrors[field.name] || field.helper || ""}
                                      sx={inputSx}
                                    >
                                      {field.select &&
                                        ["home", "office", "shop", "other"].map((v) => (
                                          <MenuItem key={v} value={v}>
                                            {v.charAt(0).toUpperCase() + v.slice(1)}
                                          </MenuItem>
                                        ))}
                                    </TextField>
                                  </Grid>
                                ))}

                                <Grid item xs={12}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Checkbox
                                      checked={addressForm.is_default}
                                      name="is_default"
                                      onChange={handleAddressFormChange}
                                      sx={{ color: BRAND.muted, "&.Mui-checked": { color: BRAND.red } }}
                                    />
                                    <Typography sx={{ color: BRAND.charcoal, fontWeight: 600 }}>
                                      Set as default address
                                    </Typography>
                                  </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                    <Button
                                      variant="contained"
                                      onClick={saveAddress}
                                      disabled={savingAddress}
                                      sx={{ borderRadius: 999, px: 3, py: 1.2, fontWeight: 800, background: BRAND.gradPrimary }}
                                    >
                                      {savingAddress ? "Saving..." : "Save Address"}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      onClick={() => {
                                        setShowAddressForm(false);
                                        setAddressForm(emptyAddressForm);
                                        setAddressErrors({});
                                      }}
                                      sx={{ borderRadius: 999, px: 3, color: BRAND.redDeep, fontWeight: 700 }}
                                    >
                                      Cancel
                                    </Button>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </MotionBox>
                          )}
                        </AnimatePresence>
                      </CardContent>
                    </SectionCard>
                  </MotionBox>
                )}
              </AnimatePresence>

              <SectionCard>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <SectionHeader icon={<RestaurantMenuIcon />} title="Order Items" subtitle={`${itemCount} ${itemCount === 1 ? "item" : "items"} in your cart`} />

                  <Stack>
                    {cart.items.map((item, idx) => (
                      <MotionBox
                        key={item.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        sx={{ py: 2, borderBottom: idx < cart.items.length - 1 ? "1px dashed rgba(122,14,14,0.15)" : "none" }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 800, color: BRAND.charcoal }}>
                              {item.item_name}
                            </Typography>
                            {item.variant_name && (
                              <Chip size="small" label={item.variant_name} sx={{ mt: 0.5, bgcolor: BRAND.creamDeep, color: BRAND.charcoal, fontWeight: 700, fontSize: 11, height: 20 }} />
                            )}
                            {item.notes && (
                              <Typography variant="body2" sx={{ color: BRAND.muted, fontStyle: "italic", mt: 0.5 }}>
                                “{item.notes}”
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ color: BRAND.muted, mt: 0.5 }}>
                              Qty {item.quantity} × {formatDollar(item.unit_price)}
                            </Typography>
                          </Box>

                          <Typography sx={{ fontWeight: 900, fontSize: "1.05rem", color: BRAND.redDeep }}>
                            {formatDollar(item.line_total)}
                          </Typography>
                        </Stack>
                      </MotionBox>
                    ))}
                  </Stack>
                </CardContent>
              </SectionCard>

              <SectionCard>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <SectionHeader icon={<RestaurantIcon />} title="Special Instructions" subtitle="Any preferences for the chef?" />
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Example: less spicy, no onion, ring the bell"
                    sx={inputSx}
                  />
                </CardContent>
              </SectionCard>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box sx={{ position: { md: "sticky" }, top: { md: 24 } }}>
              <SectionCard>
                <Box sx={{ p: 2.5, background: BRAND.gradPrimary, color: "#fff" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "grid", placeItems: "center" }}>
                      <ReceiptLongIcon sx={{ color: "#fff" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "1.35rem" }}>
                        Order Summary
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600 }}>
                        Almost there!
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>Items Count</Typography>
                      <Typography fontWeight={700} sx={{ color: BRAND.charcoal }}>{itemCount}</Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>Service Type</Typography>
                      <Chip label={selectedService?.replace("_", " ")} size="small" sx={{ background: BRAND.gradAmber, color: "#fff", fontWeight: 700, textTransform: "capitalize" }} />
                    </Stack>

                    {selectedService === "delivery" && (
                      <Box>
                        <Typography sx={{ color: BRAND.muted, mb: 0.75, fontWeight: 600 }}>
                          Delivery Address
                        </Typography>

                        {selectedAddress ? (
                          <Alert severity="success" icon={<HomeIcon />} sx={{ borderRadius: 3, fontWeight: 600 }}>
                            {selectedAddress.street_address}, {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}
                          </Alert>
                        ) : (
                          <Alert severity="warning" sx={{ borderRadius: 3, fontWeight: 600 }}>
                            Please select or add an address.
                          </Alert>
                        )}

                        {quoteError && (
                          <Alert severity="error" sx={{ mt: 1, borderRadius: 3, fontWeight: 600 }}>
                            {quoteError}
                          </Alert>
                        )}
                      </Box>
                    )}

                    <Divider sx={{ borderStyle: "dashed", borderColor: "rgba(122,14,14,0.2)" }} />

                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>Subtotal</Typography>
                      <Typography fontWeight={700} sx={{ color: BRAND.charcoal }}>
                        {formatDollar(cart?.subtotal)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>Discount</Typography>
                      <Typography fontWeight={700} sx={{ color: "#2E7D32" }}>
                        - {formatDollar(cart?.discount)}
                      </Typography>
                    </Stack>

                    {selectedService === "delivery" && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ color: BRAND.muted, fontWeight: 600 }}>
                            Delivery Fee
                          </Typography>
                          {quoteLoading && <CircularProgress size={14} />}
                        </Stack>
                        <Typography fontWeight={800} sx={{ color: BRAND.charcoal }}>
                          {quoteLoading ? "Calculating..." : formatDollar(deliveryFee)}
                        </Typography>
                      </Stack>
                    )}

                    <Divider sx={{ borderStyle: "dashed", borderColor: "rgba(122,14,14,0.2)" }} />

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, fontSize: "1.3rem", color: BRAND.charcoal }}>
                        Total 
                      </Typography>
                      <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "1.6rem", color: BRAND.redDeep }}>
                        {formatDollar(finalTotal)}
                      </Typography>
                    </Stack>

                    <Card sx={{ borderRadius: 3, background: "linear-gradient(135deg, #FFF1E0 0%, #FBE9D2 100%)", border: "1px solid rgba(255,143,0,0.3)", boxShadow: "none", mt: 1 }}>
                      <CardContent sx={{ py: 1.75 }}>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ width: 40, height: 40, borderRadius: "50%", background: BRAND.gradAmber, display: "grid", placeItems: "center" }}>
                              <PaymentIcon sx={{ color: "#fff", fontSize: 20 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontWeight: 800, color: BRAND.charcoal }}>Payment Mode</Typography>
                              <Typography variant="caption" sx={{ color: BRAND.muted }}>
                                Choose one payment option
                              </Typography>
                            </Box>
                          </Stack>

                          <Grid container spacing={1.5}>
                            {[
                              { value: "stripe", label: "Stripe" },
                              { value: "cod", label: "Cash on Delivery" },
                            ].map((option) => {
                              const selected = paymentMethod === option.value;

                              return (
                                <Grid item xs={12} sm={6} key={option.value}>
                                  <Card
                                    onClick={() => setPaymentMethod(option.value)}
                                    sx={{
                                      cursor: "pointer",
                                      borderRadius: 3,
                                      border: selected ? `2px solid ${BRAND.red}` : "1px solid rgba(122,14,14,0.14)",
                                      background: selected ? "#fff" : "rgba(255,255,255,0.65)",
                                    }}
                                  >
                                    <CardContent sx={{ py: 1.4, px: 1.5 }}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Radio checked={selected} sx={{ p: 0, color: BRAND.muted, "&.Mui-checked": { color: BRAND.red } }} />
                                        <Typography sx={{ fontWeight: 800, color: BRAND.charcoal, fontSize: 14 }}>
                                          {option.label}
                                        </Typography>
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Button
                      component={motion.button}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={
                        placingOrder ||
                        quoteLoading ||
                        (selectedService === "delivery" && (!selectedAddressId || !deliveryQuote?.quote_id))
                      }
                      onClick={placeOrder}
                      sx={{
                        mt: 2,
                        borderRadius: 999,
                        py: 1.5,
                        fontWeight: 900,
                        fontSize: "1.05rem",
                        letterSpacing: 0.5,
                        background: BRAND.gradPrimary,
                        boxShadow: "0 14px 32px rgba(122,14,14,0.4)",
                        "&.Mui-disabled": {
                          background: "rgba(0,0,0,0.12)",
                          color: "rgba(0,0,0,0.35)",
                        },
                      }}
                    >
                      {placingOrder ? "Placing Order..." : "Place Order"}
                    </Button>

                    <Typography variant="caption" sx={{ color: BRAND.muted, textAlign: "center", fontStyle: "italic" }}>
                      Stripe payments are processed securely before order placement.
                    </Typography>
                  </Stack>
                </CardContent>
              </SectionCard>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <StripePaymentDialog
  open={stripeDialogOpen}
  onClose={() => {
    if (!paymentIntentLoading && !placingOrder) {
      setStripeDialogOpen(false);
    }
  }}
  amount={paymentIntentData?.amount || finalTotal.toFixed(2)}
  currency={paymentIntentData?.currency || "inr"}
  clientSecret={paymentIntentData?.client_secret || ""}
  paymentIntentId={paymentIntentData?.payment_intent_id || ""}
  loadingIntent={paymentIntentLoading}
  intentError={paymentIntentError}
  onPaymentSuccess={handleStripePaymentSuccess}
/>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: 3, fontWeight: 600, boxShadow: "0 12px 30px rgba(0,0,0,0.2)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckoutPage;